import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import styles from "./Admin.module.css";
import { Tooltip } from "../shared/Tooltip";

/**
 * A modal dialog built on the native `<dialog>` element, so focus trapping, the top-layer
 * backdrop and closing on Escape all come from the browser rather than hand-rolled JavaScript.
 * `onClose` fires from the dialog's own `close` event, so Escape, the close button (which calls
 * `.close()`) and a backdrop click all funnel through the same path.
 *
 * @param props.closeIcon - Close with a round "×" in the top right corner rather than a "Close" button.
 * @param props.headerActions - Small controls for the dialog as a whole (an export), beside the close button.
 * @param props.tall - Keep the dialog at a fixed height, so content that shrinks (a filtered list) does not shrink it with it. Its
 *   header (and toolbar) stay in place while only the body scrolls.
 * @param props.toolbar - Controls under the title that stay in place while the body scrolls, such as a search; with `tall`.
 * @param props.lit - Give the dialog the page's card edge: the brand stripe across the top, a faint brand ring and glow.
 */
export function Modal({
  title,
  onClose,
  children,
  closeOnBackdropClick = true,
  closeOnEscape = true,
  wide = false,
  showClose = true,
  closeIcon = false,
  headerActions,
  tall = false,
  toolbar,
  lit = false,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  closeOnBackdropClick?: boolean;
  closeOnEscape?: boolean;
  wide?: boolean;
  showClose?: boolean;
  closeIcon?: boolean;
  headerActions?: React.ReactNode;
  tall?: boolean;
  toolbar?: React.ReactNode;
  lit?: boolean;
}): React.JSX.Element {
  const { t } = useTranslation();
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    dialogRef.current?.showModal();
    dialogRef.current?.querySelector<HTMLElement>("[data-autofocus]")?.focus();
  }, []);

  const handleBackdropClick = (event: React.MouseEvent<HTMLDialogElement>) => {
    if (closeOnBackdropClick && event.target === dialogRef.current) {
      dialogRef.current?.close();
    }
  };

  const handleCancel = (event: React.SyntheticEvent<HTMLDialogElement>) => {
    if (!closeOnEscape) {
      event.preventDefault();
    }
  };

  return (
    <dialog
      ref={dialogRef}
      className={[styles.dialog, wide ? styles.dialogWide : "", tall ? styles.dialogTall : "", lit ? styles.dialogLit : ""].filter(Boolean).join(" ")}
      onClose={onClose}
      onClick={handleBackdropClick}
      onCancel={handleCancel}
      aria-labelledby="modal-heading"
    >
      <div className={styles.dialogTop}>
        <div className={styles.dialogHeader}>
          <h2 id="modal-heading">{title}</h2>
          {(headerActions !== undefined || showClose) && (
            <div className={styles.dialogHeaderActions}>
              {headerActions}
              {showClose &&
                (closeIcon ? (
                  <Tooltip label={t("admin.common.close")} placement="below" align="end">
                    <button type="button" className={styles.dialogIconButton} onClick={() => dialogRef.current?.close()} aria-label={t("admin.common.close")}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" aria-hidden="true">
                        <path d="M6 6l12 12M18 6 6 18" />
                      </svg>
                    </button>
                  </Tooltip>
                ) : (
                  <button type="button" className="button button-secondary" onClick={() => dialogRef.current?.close()} aria-label={t("admin.common.close")}>
                    {t("admin.common.close")}
                  </button>
                ))}
            </div>
          )}
        </div>
        {toolbar}
      </div>
      <div className={styles.dialogBody}>{children}</div>
    </dialog>
  );
}
